import React, { useState, useEffect } from "react";
import LoginPagePNG from "../assets/images/loginPage.png";
import LogoSVG from "../assets/icons/logo.svg";
import { ForgotPasswordModal } from "../components/modals/ForgotPasswordModal";
import { TwoFactorSetupModal } from "../components/modals/TwoFactorSetupModal";
import { TwoFactorVerifyModal } from "../components/modals/TwoFactorVerifyModal";
import { ChangePasswordModal } from "../components/modals/ChangePasswordModal";
import { ResetPasswordModal } from "../components/modals/ResetPasswordModal";
import apiService from "../services/api";

interface LoginScreenProps {
  onLogin: (email: string, password: string) => Promise<void>;
  onGoogleLoginInit: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  forgotPasswordKey?: string;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({
  onLogin,
  onGoogleLoginInit,
  isLoading,
  error,
  forgotPasswordKey,
}) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [displayError, setDisplayError] = useState<string>("");
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [show2FASetupModal, setShow2FASetupModal] = useState(false);
  const [show2FAVerifyModal, setShow2FAVerifyModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [qrCodeData, setQrCodeData] = useState<string>("");
  const [googleSecret, setGoogleSecret] = useState<string>("");
  const [twoFAError, setTwoFAError] = useState<string>("");
  const [verifying2FA, setVerifying2FA] = useState<boolean>(false);

  React.useEffect(() => {
    if (error) {
      setDisplayError(error);
    }
  }, [error]);

  // A failed Google sign-in explains itself on the callback screen and is then
  // redirected away from a few seconds later, so the person often lands back
  // here having half-read it. Show it again, and clear it immediately so it
  // does not reappear on the next visit as a stale complaint.
  useEffect(() => {
    try {
      const carried = sessionStorage.getItem("loginError");
      if (carried) {
        sessionStorage.removeItem("loginError");
        setDisplayError(carried);
      }
    } catch (e) {
      // Storage can be unavailable; there is simply no message to carry then.
    }
  }, []);

  // Check forgot password key when provided
  useEffect(() => {
    const checkForgotPasswordKey = async () => {
      if (forgotPasswordKey) {
        console.log("[LoginScreen] Checking forgot password key");
        try {
          const response = await apiService.checkForgotPasswordKey({ key: forgotPasswordKey });
          console.log("[LoginScreen] Key check response:", response.message);

          if (response.message === "Invalid Token") {
            setDisplayError("Invalid Token - try again Forgot Password");
          } else {
            // Valid key, show reset password modal
            setShowResetPasswordModal(true);
          }
        } catch (err: any) {
          console.error("[LoginScreen] Key check failed:", err);
          setDisplayError("Invalid Token - try again Forgot Password");
        }
      }
    };

    checkForgotPasswordKey();
  }, [forgotPasswordKey]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev: any) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
    }

    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      await onLogin(formData.email, formData.password);
    } catch (err: any) {
      console.error("Login error:", err);

      // Check if this is a 2FA code required error (401 - user has 2FA enabled)
      if (err.response?.status === 401 && err.response?.data?.detail?.message === "2FA code is required") {
        const googleSecretFromError = err.response.data.detail.google_secret || "";
        console.log("[LoginScreen] 2FA code required, google_secret:", googleSecretFromError ? "present" : "none");
        setGoogleSecret(googleSecretFromError); // Store google_secret for verification
        setTwoFAError(""); // Fresh modal, no stale 2FA error
        setShow2FAVerifyModal(true);
        setDisplayError(""); // Clear any previous errors
        return;
      }

      // Check if this is a Password Expired error (401 - user needs to change password)
      if (err.response?.status === 401 && err.response?.data?.detail === "Password Expired") {
        console.log("[LoginScreen] Password expired, showing change password modal");
        setShowChangePasswordModal(true);
        setDisplayError(""); // Clear any previous errors
        return;
      }

      // Check if this is a 2FA setup required error (401 - user needs to setup 2FA)
      if (err.response?.status === 401 && err.response?.data?.detail?.message === "2FA setup is required") {
        const email = err.response.data.detail.email;
        const googleSecretFromError = err.response.data.detail.google_secret || "";
        console.log("[LoginScreen] 2FA setup required for:", email, "google_secret:", googleSecretFromError ? "present" : "none");

        try {
          // Call setup_2fa API to get QR code
          const setup2FAResponse = await apiService.setup2FA(email);
          console.log("[LoginScreen] Got 2FA setup data");

          // Show the 2FA setup modal with QR code
          setQrCodeData(setup2FAResponse.qr_code);
          setGoogleSecret(googleSecretFromError); // Store google_secret for later use
          setShow2FASetupModal(true);
          setDisplayError(""); // Clear any previous errors
        } catch (setupError: any) {
          console.error("[LoginScreen] Failed to setup 2FA:", setupError);
          setDisplayError("Failed to initialize 2FA setup. Please try again.");
        }
        return; // Don't process this error further
      }
    }
  };

  const handle2FAEnable = async (token: string) => {
    console.log("[LoginScreen] Enabling 2FA with token:", token);
    try {
      // Call login with 2FA token and google_secret if present
      const response = await apiService.login({
        email: formData.email,
        password: formData.password,
        twofa_code: token,
        ...(googleSecret && { google_secret: googleSecret }),
      });

      // Store token and update auth state
      localStorage.setItem("token", response.authToken);
      setShow2FASetupModal(false);
      setGoogleSecret(""); // Clear google_secret

      // Reload to reinitialize auth state
      window.location.href = "/";
    } catch (err: any) {
      console.error("[LoginScreen] Failed to enable 2FA:", err);
      setDisplayError("Failed to enable 2FA. Please verify the token and try again.");
      setShow2FASetupModal(false);
    }
  };

  const handle2FASkip = async () => {
    console.log("[LoginScreen] Skipping 2FA setup");
    try {
      // Call login with skip_2fa flag and google_secret if present
      const response = await apiService.login({
        email: formData.email,
        password: formData.password,
        skip_2fa: true,
        ...(googleSecret && { google_secret: googleSecret }),
      });

      // Store token and update auth state
      localStorage.setItem("token", response.authToken);
      setShow2FASetupModal(false);
      setGoogleSecret(""); // Clear google_secret

      // Reload to reinitialize auth state
      window.location.href = "/";
    } catch (err: any) {
      console.error("[LoginScreen] Failed to skip 2FA:", err);
      setDisplayError("Failed to skip 2FA setup. Please try again.");
      setShow2FASetupModal(false);
    }
  };

  const handle2FAVerify = async (token: string) => {
    console.log("[LoginScreen] Verifying 2FA code");
    setTwoFAError("");
    setVerifying2FA(true);
    try {
      // Call login with 2FA token and google_secret if present
      const response = await apiService.login({
        email: formData.email,
        password: formData.password,
        twofa_code: token,
        ...(googleSecret && { google_secret: googleSecret }),
      });

      // Store token and update auth state
      localStorage.setItem("token", response.authToken);
      setShow2FAVerifyModal(false);
      setGoogleSecret(""); // Clear google_secret

      // Reload to reinitialize auth state
      window.location.href = "/";
    } catch (err: any) {
      console.error("[LoginScreen] Failed to verify 2FA:", err);
      // Keep the modal open with an inline error so the user can correct the
      // code without re-entering their email/password.
      setTwoFAError("Invalid 2FA code. Please try again.");
    } finally {
      setVerifying2FA(false);
    }
  };

  const handle2FACancel = () => {
    console.log("[LoginScreen] Cancelling 2FA setup");
    setShow2FASetupModal(false);
    setQrCodeData("");
    setGoogleSecret("");
  };

  const handlePasswordChanged = () => {
    console.log("[LoginScreen] Password changed successfully");
    setShowChangePasswordModal(false);
    setDisplayError("Password changed successfully. Please login with your new password.");
  };

  const handlePasswordReset = () => {
    console.log("[LoginScreen] Password reset successfully");
    setShowResetPasswordModal(false);
    setDisplayError("Password reset successfully. Please login with your new password.");
    // Redirect to clean login page
    window.history.pushState({}, '', '/');
  };

  return (
    <>
      <div className="flex flex-wrap m-0 w-full min-h-screen bg-brand-green">
        <div className="hidden sm:block sm:w-5/12 lg:w-1/3 p-0 relative">
          <img
            src={LoginPagePNG}
            alt="Login page design"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="w-full sm:w-7/12 lg:w-2/3 flex flex-col">
          <div className="my-auto">
            <div className="w-full sm:w-8/12 lg:w-5/12 mx-auto px-4">
              <div className="text-center mb-10">
                <img src={LogoSVG} alt="PREDICTIX CARE" className="h-20 mx-auto" />
              </div>

              <div className="text-brand-darkTeal text-base font-semibold mb-6">
                Let's get started
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-5">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email"
                    className="w-full px-5 py-3 rounded-[20px] border border-gray-300 bg-[#eef2f7] text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                  />
                  {formErrors.email && (
                    <div className="text-red-600 text-xs mt-1">
                      {formErrors.email}
                    </div>
                  )}
                </div>

                <div className="mb-5">
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Password"
                    className="w-full px-5 py-3 rounded-[20px] border border-gray-300 bg-[#eef2f7] text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                  />
                  {formErrors.password && (
                    <div className="text-red-600 text-xs mt-1">
                      {formErrors.password}
                    </div>
                  )}
                </div>

                <div className="ps-3 mb-5">
                  <button
                    type="button"
                    onClick={() => setShowForgotPasswordModal(true)}
                    className="bg-transparent border-none text-brand-teal text-sm font-normal cursor-pointer p-0 hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>

                {displayError && (
                  <div className={`p-3 rounded-lg mb-5 text-sm ${
                    displayError.toLowerCase().includes('successfully')
                      ? 'bg-green-50 border border-green-300 text-green-800'
                      : 'bg-red-50 border border-red-300 text-red-800'
                  }`}>
                    {!displayError.toLowerCase().includes('successfully') && <strong>Login Error:</strong>} {displayError}
                  </div>
                )}

                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full px-5 py-3 border-none rounded-[20px] text-sm font-semibold bg-brand-orange text-white hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed transition-opacity"
                  >
                    {isLoading ? "Logging in..." : "Login"}
                  </button>

                  <button
                    type="button"
                    onClick={onGoogleLoginInit}
                    className="w-full px-5 py-3 border-none rounded-[20px] text-sm font-semibold bg-brand-orange text-white hover:opacity-90 transition-opacity"
                  >
                    Google login
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
      />

      <TwoFactorSetupModal
        isOpen={show2FASetupModal}
        onClose={handle2FACancel}
        qrCode={qrCodeData}
        onEnable={handle2FAEnable}
        onSkip={handle2FASkip}
      />

      <TwoFactorVerifyModal
        isOpen={show2FAVerifyModal}
        onClose={() => {
          setShow2FAVerifyModal(false);
          setGoogleSecret("");
          setTwoFAError("");
        }}
        onVerify={handle2FAVerify}
        error={twoFAError}
        isVerifying={verifying2FA}
      />

      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        email={formData.email}
        onPasswordChanged={handlePasswordChanged}
      />

      <ResetPasswordModal
        isOpen={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
        resetKey={forgotPasswordKey || ""}
        onPasswordReset={handlePasswordReset}
      />
    </>
  );
};
